import "mapbox-gl/dist/mapbox-gl.css";
import type { GeoJSONSource, Map as MapboxMap, Marker } from "mapbox-gl";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./map.module.css";

/** `[longitude, latitude]`, GeoJSON order. */
export type LngLat = [number, number];

export interface MapZone {
  id: string;
  name: string;
  /** Outer rings, one per polygon of the zone. */
  rings: LngLat[][];
  /** Drawn faintly: an inactive zone, or one not being looked at. */
  muted?: boolean;
  /** Shown beside the name on the zone's label, e.g. its price ("KES 4,500.00"). */
  note?: string;
}

export interface MapPoint {
  id: string;
  lng: number;
  lat: number;
  label?: string;
  /**
   * `danger`: drawn red, e.g. a place no zone covers. The dispatch board (P8-53) colours
   * deliveries by where they stand: `warning` to plan, `info` assigned, `accent` under way,
   * `success` done, `muted` closed; `site` is a fill site (a dark dot).
   */
  tone?: "default" | "danger" | "warning" | "info" | "accent" | "success" | "muted" | "site";
  /** drawn larger, above the rest */
  selected?: boolean;
  /** a short label beside the point, e.g. a fill site's name and distance (P8-53) */
  tag?: string;
}

/**
 * A truck where its phone last placed it (P8-51): `moving`, `idle` (on shift, not moving), `held`
 * (its delivery is suspended) or `stale` (not heard from lately, drawn faded).
 */
export interface MapTruck {
  id: string;
  lng: number;
  lat: number;
  state: "moving" | "idle" | "held" | "stale";
  /** shown beside it, e.g. its plate */
  label?: string;
  /** read by screen readers and on hover: "KCA 778M, moving at 34 km/h, seen 12 s ago" */
  title: string;
}

/**
 * A tank with a level sensor (P8-51): `ok`, `low` (in a low-level episode: red, with its name) or
 * `none` (no reading). Drawn as a chip with a small gauge and the level.
 */
export interface MapTank {
  id: string;
  lng: number;
  lat: number;
  state: "ok" | "low" | "none";
  /** how full the gauge is drawn, 0 to 100; null: empty */
  gauge: number | null;
  /** on the chip: "72%", "14% · Kilimani Heights", "—" */
  text: string;
  /** read by screen readers and on hover */
  title: string;
}

/** lucide's truck, drawn inside a truck marker */
const TRUCK_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>';

/** One place being looked at: a dot with its name beside it. `danger`: it can't be served. */
export interface MapPin {
  lng: number;
  lat: number;
  label: string;
  note?: string;
  tone?: "default" | "danger";
}

/** Where a zone's label goes: the middle of its largest outline's bounding box. */
export function labelPoint(rings: LngLat[][]): LngLat | null {
  let best: { area: number; at: LngLat } | null = null;
  for (const ring of rings) {
    if (!ring.length) continue;
    const xs = ring.map((p) => p[0]);
    const ys = ring.map((p) => p[1]);
    const [w, e, s, n] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    const area = (e - w) * (n - s);
    if (!best || area > best.area) best = { area, at: [(w + e) / 2, (s + n) / 2] };
  }
  return best?.at ?? null;
}

/** The default base map: Mapbox Streets (P8-69). */
export const MAPBOX_STREETS = "mapbox://styles/mapbox/streets-v12";
/** Mapbox Light, muted so zones and prices stand out (the design's map, ADR 0010). */
export const MAPBOX_LIGHT = "mapbox://styles/mapbox/light-v11";

/** The base maps the layers button offers (P8-69). */
export const BASE_MAPS = [
  { id: "streets", label: "Streets", style: MAPBOX_STREETS },
  { id: "light", label: "Light", style: MAPBOX_LIGHT },
  { id: "dark", label: "Dark", style: "mapbox://styles/mapbox/dark-v11" },
  { id: "satellite", label: "Satellite", style: "mapbox://styles/mapbox/satellite-streets-v12" },
] as const;
export type BaseMapId = (typeof BASE_MAPS)[number]["id"];

const BASE_MAP_KEY = "majitap.map.base";

/** The base map this browser last chose, if any. */
export function savedBaseMap(): BaseMapId | null {
  try {
    const id = globalThis.localStorage?.getItem(BASE_MAP_KEY);
    return BASE_MAPS.find((b) => b.id === id)?.id ?? null;
  } catch {
    return null;
  }
}

/** Remembers the base map for every map this browser opens next. */
export function saveBaseMap(id: BaseMapId) {
  try {
    globalThis.localStorage?.setItem(BASE_MAP_KEY, id);
  } catch {
    // private window or blocked storage: the choice holds for this map only
  }
}

const settings: { accessToken: string | null; style: string } = { accessToken: null, style: MAPBOX_STREETS };

/**
 * Mapbox's public access token (`pk.…`, restricted to the app's URLs in the Mapbox account), and
 * optionally another style. An app calls this once, before any map shows; without a token the
 * maps say so instead of loading.
 */
export function configureMaps(options: { accessToken: string | undefined | null; style?: string }) {
  settings.accessToken = options.accessToken?.trim() || null;
  if (options.style) settings.style = options.style;
}

/** Central Nairobi: where every map opens, and where Back to Nairobi returns (P8-69). */
const NAIROBI_CENTER: LngLat = [36.8219, -1.2921];
const NAIROBI_ZOOM = 11;
/**
 * The Nairobi metro area, `[west, south]` to `[east, north]`: Limuru and Thika to the north,
 * Machakos and Kitengela to the south. Maps can't be panned or zoomed out past it.
 */
export const NAIROBI_BOUNDS: [LngLat, LngLat] = [
  [36.45, -1.75],
  [37.35, -0.85],
];

/** What a map draws that the viewer can hide (P8-69). */
export type MapLayerId = "zones" | "points" | "routes" | "trail" | "trucks" | "tanks";

/** The map layers behind each kind (trucks, tanks, and the zones' and points' labels are markers). */
const LAYER_IDS: Record<MapLayerId, string[]> = {
  zones: ["zones-fill", "zones-line"],
  points: ["points"],
  routes: ["route", "lines"],
  trail: ["trail"],
  trucks: [],
  tanks: [],
};

/** The kinds a map can hide: only those it has something of, in drawing order. */
export function mapLayers(map: {
  zones?: readonly unknown[];
  points?: readonly unknown[];
  pointsName?: string;
  route?: readonly unknown[] | null;
  lines?: readonly unknown[];
  trail?: readonly unknown[] | null;
  trucks?: readonly unknown[];
  trucksName?: string;
  tanks?: readonly unknown[];
}): { id: MapLayerId; label: string }[] {
  const layers: { id: MapLayerId; label: string; shown: boolean }[] = [
    { id: "zones", label: "Zones", shown: Boolean(map.zones?.length) },
    { id: "points", label: map.pointsName ?? "Places", shown: Boolean(map.points?.length) },
    { id: "routes", label: "Routes", shown: (map.route?.length ?? 0) >= 2 || Boolean(map.lines?.length) },
    { id: "trail", label: "Trail", shown: (map.trail?.length ?? 0) >= 2 },
    { id: "tanks", label: "Tanks", shown: Boolean(map.tanks?.length) },
    { id: "trucks", label: map.trucksName ?? "Trucks", shown: Boolean(map.trucks?.length) },
  ];
  return layers.filter((l) => l.shown).map(({ id, label }) => ({ id, label }));
}

/** The outer rings of a GeoJSON Polygon or MultiPolygon, as the map draws them. */
export function ringsOf(geometry: unknown): LngLat[][] {
  const g = geometry as { type?: string; coordinates?: unknown } | null;
  if (g?.type === "Polygon") return [((g.coordinates as LngLat[][]) ?? [])[0] ?? []];
  if (g?.type === "MultiPolygon") return ((g.coordinates as LngLat[][][]) ?? []).map((p) => p[0] ?? []);
  return [];
}

/** A drawn outline (open: no repeated first point) as a closed GeoJSON Polygon the API takes. */
export function polygonOf(vertices: LngLat[]): { type: "Polygon"; coordinates: LngLat[][] } {
  return { type: "Polygon", coordinates: [[...vertices, vertices[0]]] };
}

/** A closed ring without its closing point, for editing. */
export const openRing = (ring: LngLat[]): LngLat[] =>
  ring.length > 1 && ring[0][0] === ring.at(-1)?.[0] && ring[0][1] === ring.at(-1)?.[1] ? ring.slice(0, -1) : ring;

/**
 * Mapbox marks every marker element `role="img"`, named "Map marker". A marker that is a button
 * (a zone's pill, a corner) stays a button, with its own name.
 */
function keepButton(el: HTMLElement, name: string) {
  el.removeAttribute("role");
  el.setAttribute("aria-label", name);
}

const overlaps = (a: DOMRect, b: DOMRect) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const color = (name: string, fallback: string) =>
  (typeof document !== "undefined" && getComputedStyle(document.documentElement).getPropertyValue(name).trim()) ||
  fallback;

export interface ZoneMapProps {
  zones?: MapZone[];
  points?: MapPoint[];
  /** What the points are, for the layers button: "Deliveries and fill sites", "Fill sites"… Default "Places". */
  pointsName?: string;
  selectedId?: string | null;
  onSelectZone?: (id: string) => void;
  /** An outline being drawn or edited: click the map to add a corner, drag a corner to move it, click one to remove it. */
  draft?: LngLat[] | null;
  onDraftChange?: (vertices: LngLat[]) => void;
  /** A map click when no outline is being drawn (the point picker). */
  onMapClick?: (at: LngLat) => void;
  /** Where to look: the selected zone, else everything. */
  focus?: LngLat[] | null;
  /** Each zone's name (and note) on the map; clicking one selects the zone. */
  zoneLabels?: boolean;
  /** One place being looked at, drawn above everything else. */
  pin?: MapPin | null;
  /** A trip drawn as a line, e.g. from the fill site to the customer (P8-48). */
  route?: LngLat[] | null;
  /** Thin guide lines, e.g. from a delivery to the fill sites it could use (P8-53). */
  lines?: LngLat[][];
  /** Trucks where they are now (P8-51); moved in place on each refresh. */
  trucks?: MapTruck[];
  /** What the trucks are, for the layers button: "Your delivery" in the customer app. Default "Trucks". */
  trucksName?: string;
  selectedTruckId?: string | null;
  onSelectTruck?: (id: string) => void;
  /** Tanks with a level sensor (P8-51), under the trucks. */
  tanks?: MapTank[];
  selectedTankId?: string | null;
  onSelectTank?: (id: string) => void;
  /** Shown beside the selected tank, e.g. its level and a link to its customer. */
  tankCallout?: ReactNode;
  /** Shown beside the selected truck, in place of its label: what it's doing (P8-51). */
  truckCallout?: ReactNode;
  /** A point clicked on the map, e.g. a delivery on the dispatch board (P8-53). */
  onSelectPoint?: (id: string) => void;
  /** Where a truck has been, oldest first: drawn solid under the trucks (P8-51). */
  trail?: LngLat[] | null;
  /** `accent` when the trail is the thing looked at (the live map); dark beside a route (a trip). */
  trailTone?: "foreground" | "accent";
  height?: string;
  /** A Mapbox style URL to open on, instead of the viewer's base map or the configured one. */
  mapStyle?: string;
  /** What the colours mean, shown in the bottom-left corner. */
  legend?: ReactNode;
  /** A button under the zoom that frames every zone and point again. */
  fitAll?: boolean;
  /**
   * A small preview that opens a bigger map elsewhere: no buttons on it (zoom, Back to Nairobi,
   * layers), so they don't crowd it or cover the page's own. Pinch and drag still work.
   */
  compact?: boolean;
  label: string;
  children?: ReactNode;
}

/**
 * Zones, points and an editable outline on a map (Mapbox GL JS). The map loads in the browser
 * only; on the server it renders its frame.
 */
export function ZoneMap({
  zones = [],
  points = [],
  pointsName,
  selectedId = null,
  onSelectZone,
  draft = null,
  onDraftChange,
  onMapClick,
  focus = null,
  zoneLabels = false,
  pin = null,
  route = null,
  lines = [],
  trucks = [],
  trucksName,
  selectedTruckId = null,
  onSelectTruck,
  tanks = [],
  selectedTankId = null,
  onSelectTank,
  tankCallout,
  truckCallout,
  onSelectPoint,
  trail = null,
  trailTone = "foreground",
  height = "28rem",
  mapStyle,
  legend,
  fitAll = false,
  compact = false,
  label,
  children,
}: ZoneMapProps) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxMap | null>(null);
  const markers = useRef<Marker[]>([]);
  const labelMarkers = useRef<Marker[]>([]);
  const truckMarkers = useRef(new Map<string, { marker: Marker; el: HTMLButtonElement; host: HTMLDivElement }>());
  /** where the selected truck's callout goes */
  const [truckHost, setTruckHost] = useState<HTMLDivElement | null>(null);
  /** hides the labels with no room (set by the trucks, rerun when the points' tags change) */
  const declutterTrucks = useRef<(() => void) | null>(null);
  const lib = useRef<typeof import("mapbox-gl") | null>(null);
  const tankMarkers = useRef(
    new Map<string, { marker: Marker; el: HTMLDivElement; chip: HTMLButtonElement; host: HTMLDivElement }>(),
  );
  /** where the selected tank's callout goes */
  const [tankHost, setTankHost] = useState<HTMLDivElement | null>(null);
  const handlers = useRef({
    onSelectZone,
    onDraftChange,
    onMapClick,
    onSelectTruck,
    onSelectTank,
    onSelectPoint,
    draft,
  });
  handlers.current = { onSelectZone, onDraftChange, onMapClick, onSelectTruck, onSelectTank, onSelectPoint, draft };
  /** Counts the styles loaded: our sources and layers are (re)added on each, so the drawing reruns. */
  const [styleLoads, setStyleLoads] = useState(0);
  const loaded = styleLoads > 0;
  const accessToken = settings.accessToken;
  const [style, setStyle] = useState(
    () => mapStyle ?? BASE_MAPS.find((b) => b.id === savedBaseMap())?.style ?? settings.style,
  );
  const initialStyle = useRef(style);
  const [layersOpen, setLayersOpen] = useState(false);
  const [hidden, setHidden] = useState<ReadonlySet<MapLayerId>>(new Set());
  const layers = mapLayers({ zones, points, pointsName, route, lines, trail, trucks, trucksName, tanks });
  const withButtons = useRef(!compact);

  useEffect(() => {
    if (mapStyle) setStyle(mapStyle);
  }, [mapStyle]);

  // create the map once (Mapbox GL loads lazily, in its own chunk)
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    void import("mapbox-gl").then((loadedModule) => {
      if (cancelled || !box.current) return;
      // the package is UMD: bundlers expose it as the default export, or as the module itself
      const mapboxgl = ((loadedModule as unknown as { default?: unknown }).default ??
        loadedModule) as typeof loadedModule;
      lib.current = mapboxgl;
      const m = new mapboxgl.Map({
        container: box.current,
        accessToken,
        style: initialStyle.current,
        center: NAIROBI_CENTER,
        zoom: NAIROBI_ZOOM,
        maxBounds: NAIROBI_BOUNDS,
      });
      if (withButtons.current) m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
      let listening = false;
      // on the first style and again after each base-map change, which drops our sources and layers
      m.on("style.load", () => {
        const accent = color("--accent", "#0b6bcb");
        const muted = color("--text-secondary", "#6b7280");
        const danger = color("--danger", "#e7000b");
        const warning = color("--warning", "#b45309");
        const info = color("--info", "#0084d1");
        const success = color("--success", "#16764a");
        const foreground = color("--foreground", "#090b0c");
        const empty = { type: "FeatureCollection" as const, features: [] };
        m.addSource("zones", { type: "geojson", data: empty });
        m.addSource("points", { type: "geojson", data: empty });
        m.addSource("draft", { type: "geojson", data: empty });
        m.addSource("route", { type: "geojson", data: empty });
        m.addSource("lines", { type: "geojson", data: empty });
        m.addSource("trail", { type: "geojson", data: empty });
        m.addLayer({
          id: "zones-fill",
          type: "fill",
          source: "zones",
          paint: {
            "fill-color": ["case", ["get", "selected"], accent, ["get", "muted"], muted, accent],
            "fill-opacity": ["case", ["get", "selected"], 0.28, ["get", "muted"], 0.06, 0.14],
          },
        });
        m.addLayer({
          id: "zones-line",
          type: "line",
          source: "zones",
          paint: {
            "line-color": ["case", ["get", "muted"], muted, accent],
            "line-width": ["case", ["get", "selected"], 3, 1.5],
          },
        });
        m.addLayer({
          id: "lines",
          type: "line",
          source: "lines",
          paint: { "line-color": accent, "line-width": 1.5, "line-opacity": 0.6, "line-dasharray": [2, 2] },
        });
        m.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": accent, "line-width": 4, "line-opacity": 0.85 },
        });
        m.addLayer({
          id: "trail",
          type: "line",
          source: "trail",
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": color("--foreground", "#090b0c"), "line-width": 3, "line-opacity": 0.7 },
        });
        m.addLayer({
          id: "points",
          type: "circle",
          source: "points",
          paint: {
            "circle-radius": ["case", ["get", "selected"], 9, ["==", ["get", "tone"], "default"], 5, 6],
            "circle-color": [
              "match",
              ["get", "tone"],
              "danger",
              danger,
              "warning",
              warning,
              "info",
              info,
              "accent",
              accent,
              "success",
              success,
              "muted",
              muted,
              "site",
              foreground,
              "#ffffff",
            ],
            "circle-opacity": ["case", ["==", ["get", "tone"], "muted"], 0.5, 1],
            "circle-stroke-color": ["case", ["==", ["get", "tone"], "default"], accent, "#ffffff"],
            "circle-stroke-width": ["case", ["get", "selected"], 3, 2],
          },
        });
        m.addLayer({
          id: "draft-fill",
          type: "fill",
          source: "draft",
          paint: { "fill-color": accent, "fill-opacity": 0.2 },
        });
        m.addLayer({
          id: "draft-line",
          type: "line",
          source: "draft",
          paint: { "line-color": accent, "line-width": 2, "line-dasharray": [2, 1] },
        });
        setStyleLoads((n) => n + 1);
        if (listening) return;
        listening = true;
        m.on("click", "zones-fill", (e) => {
          if (handlers.current.draft) return;
          const feature = e.features?.[0] as { properties?: { id?: string } | null } | undefined;
          const id = feature?.properties?.id;
          if (id) handlers.current.onSelectZone?.(id);
        });
        m.on("click", "points", (e) => {
          const feature = e.features?.[0] as { properties?: { id?: string } | null } | undefined;
          const id = feature?.properties?.id;
          if (id && handlers.current.onSelectPoint) {
            handlers.current.onSelectPoint(id);
            e.preventDefault();
          }
        });
        m.on("click", (e) => {
          if (e.defaultPrevented) return;
          const at: LngLat = [e.lngLat.lng, e.lngLat.lat];
          const { draft: d, onDraftChange: change, onMapClick: pick } = handlers.current;
          if (d) change?.([...d, at]);
          else pick?.(at);
        });
      });
      map.current = m;
    });
    return () => {
      cancelled = true;
      map.current?.remove();
      map.current = null;
      setStyleLoads(0);
    };
  }, []);

  // a new base map: Mapbox reloads the style, and style.load puts our layers back
  useEffect(() => {
    const m = map.current;
    if (!m || !loaded || initialStyle.current === style) return;
    initialStyle.current = style;
    // diff: false rebuilds the style, so style.load fires; Mapbox fills the font options from the map's own
    m.setStyle(style, { diff: false } as Parameters<MapboxMap["setStyle"]>[1]);
  }, [loaded, style]);

  // the kinds the viewer hid; markers are hidden through the frame's classes
  useEffect(() => {
    const m = map.current;
    if (!m || styleLoads === 0) return;
    for (const [kind, ids] of Object.entries(LAYER_IDS) as [MapLayerId, string[]][]) {
      for (const id of ids) {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", hidden.has(kind) ? "none" : "visible");
      }
    }
  }, [styleLoads, hidden]);

  // zones, points and the draft, whenever they change
  useEffect(() => {
    {
      const m = map.current;
      // while a new base map loads, our sources are gone: style.load adds them back and this reruns
      if (!m || styleLoads === 0 || !m.getSource("zones")) return;
      (m.getSource("zones") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features: zones.flatMap((z) =>
          z.rings
            .filter((r) => r.length >= 4)
            .map((ring) => ({
              type: "Feature" as const,
              properties: { id: z.id, name: z.name, selected: z.id === selectedId, muted: Boolean(z.muted) },
              geometry: { type: "Polygon" as const, coordinates: [ring] },
            })),
        ),
      });
      (m.getSource("points") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features: points.map((p) => ({
          type: "Feature" as const,
          properties: { id: p.id, label: p.label ?? "", tone: p.tone ?? "default", selected: Boolean(p.selected) },
          geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
        })),
      });
      (m.getSource("route") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features:
          route && route.length >= 2
            ? [
                {
                  type: "Feature" as const,
                  properties: {},
                  geometry: { type: "LineString" as const, coordinates: route },
                },
              ]
            : [],
      });
      (m.getSource("lines") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features: lines
          .filter((l) => l.length >= 2)
          .map((l) => ({
            type: "Feature" as const,
            properties: {},
            geometry: { type: "LineString" as const, coordinates: l },
          })),
      });
      m.setPaintProperty(
        "trail",
        "line-color",
        trailTone === "accent" ? color("--accent", "#0b6bcb") : color("--foreground", "#090b0c"),
      );
      m.setPaintProperty("trail", "line-opacity", trailTone === "accent" ? 0.9 : 0.7);
      (m.getSource("trail") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features:
          trail && trail.length >= 2
            ? [
                {
                  type: "Feature" as const,
                  properties: {},
                  geometry: { type: "LineString" as const, coordinates: trail },
                },
              ]
            : [],
      });
      (m.getSource("draft") as GeoJSONSource).setData({
        type: "FeatureCollection",
        features:
          draft && draft.length >= 3
            ? [{ type: "Feature" as const, properties: {}, geometry: polygonOf(draft) }]
            : draft && draft.length === 2
              ? [
                  {
                    type: "Feature" as const,
                    properties: {},
                    geometry: { type: "LineString" as const, coordinates: draft },
                  },
                ]
              : [],
      });
      // one draggable marker per corner of the draft
      for (const mk of markers.current) mk.remove();
      markers.current = [];
      const mapboxgl = lib.current;
      if (draft && mapboxgl) {
        draft.forEach((v, i) => {
          const el = document.createElement("button");
          el.type = "button";
          el.className = styles.vertex;
          el.setAttribute("aria-label", `Corner ${i + 1}: drag to move, click to remove`);
          const mk = new mapboxgl.Marker({ element: el, draggable: true }).setLngLat(v).addTo(m);
          keepButton(el, `Corner ${i + 1}: drag to move, click to remove`);
          mk.on("dragend", () => {
            const at = mk.getLngLat();
            const next = [...(handlers.current.draft ?? [])];
            next[i] = [at.lng, at.lat];
            handlers.current.onDraftChange?.(next);
          });
          el.addEventListener("click", (event) => {
            event.stopPropagation();
            const current = handlers.current.draft ?? [];
            if (current.length > 3) handlers.current.onDraftChange?.(current.filter((_, j) => j !== i));
          });
          markers.current.push(mk);
        });
      }
    }
  }, [styleLoads, zones, points, selectedId, draft, route, lines, trail, trailTone]);

  // trucks: one marker each, kept across refreshes and moved, so a moving truck doesn't flicker
  useEffect(() => {
    const m = map.current;
    const mapboxgl = lib.current;
    if (!m || !mapboxgl || !loaded) return;
    const seen = new Set<string>();
    for (const t of trucks) {
      seen.add(t.id);
      let entry = truckMarkers.current.get(t.id);
      if (!entry) {
        const el = document.createElement("button");
        el.type = "button";
        el.innerHTML = `${TRUCK_SVG}<span></span>`;
        el.addEventListener("click", (event) => {
          event.stopPropagation();
          handlers.current.onSelectTruck?.(t.id);
        });
        const host = document.createElement("div");
        host.className = styles.truckCallout;
        el.append(host);
        entry = { el, host, marker: new mapboxgl.Marker({ element: el }).setLngLat([t.lng, t.lat]).addTo(m) };
        truckMarkers.current.set(t.id, entry);
      }
      entry.marker.setLngLat([t.lng, t.lat]);
      // keep Mapbox's own mapboxgl-marker classes: they position the marker on the map
      for (const c of [...entry.el.classList]) if (!c.startsWith("mapboxgl-")) entry.el.classList.remove(c);
      entry.el.classList.add(
        ...[styles.truck, styles[`truck_${t.state}`], t.id === selectedTruckId ? styles.truckSelected : ""].filter(
          Boolean,
        ),
      );
      entry.el.setAttribute("aria-pressed", String(t.id === selectedTruckId));
      entry.el.title = t.title;
      (entry.el.querySelector("span") as HTMLSpanElement).textContent = t.label ?? "";
      keepButton(entry.el, t.title);
    }
    for (const [id, entry] of truckMarkers.current) {
      if (seen.has(id)) continue;
      entry.marker.remove();
      truckMarkers.current.delete(id);
    }
    // a label that would cover a truck or another label waits for a hover; the selected truck's
    // label always shows, the others in the order given
    const declutter = () => {
      const els = [
        ...trucks.filter((t) => t.id === selectedTruckId),
        ...trucks.filter((t) => t.id !== selectedTruckId),
      ].flatMap((t) => truckMarkers.current.get(t.id)?.el ?? []);
      const taken = els.map((el) => el.getBoundingClientRect());
      for (const el of els) {
        const label = el.querySelector("span") as HTMLSpanElement;
        if (!label.textContent) continue;
        const at = label.getBoundingClientRect();
        const clear = el.getAttribute("aria-pressed") === "true" || !taken.some((r) => overlaps(at, r));
        label.classList.toggle(styles.truckLabelHidden, !clear);
        if (clear) taken.push(at);
      }
      // a point's tag gives way to the trucks
      for (const tag of box.current?.querySelectorAll<HTMLElement>(`.${styles.tag}`) ?? []) {
        const at = tag.getBoundingClientRect();
        tag.classList.toggle(
          styles.tagHidden,
          taken.some((r) => overlaps(at, r)),
        );
      }
    };
    setTruckHost(selectedTruckId ? (truckMarkers.current.get(selectedTruckId)?.host ?? null) : null);
    declutterTrucks.current = declutter;
    declutter();
    m.on("move", declutter);
    return () => {
      m.off("move", declutter);
    };
  }, [loaded, trucks, selectedTruckId]);

  // tanks: one marker each, kept across refreshes like the trucks; the callout is portalled into
  // the selected one, so it moves with it
  useEffect(() => {
    const m = map.current;
    const mapboxgl = lib.current;
    if (!m || !mapboxgl || !loaded) return;
    const seen = new Set<string>();
    for (const t of tanks) {
      seen.add(t.id);
      let entry = tankMarkers.current.get(t.id);
      if (!entry) {
        const el = document.createElement("div");
        el.className = styles.tankMarker;
        const chip = document.createElement("button");
        chip.type = "button";
        chip.innerHTML = `<span class="${styles.gauge}" aria-hidden="true"><span></span></span><span></span>`;
        chip.addEventListener("click", (event) => {
          event.stopPropagation();
          handlers.current.onSelectTank?.(t.id);
        });
        const host = document.createElement("div");
        host.className = styles.tankCallout;
        el.append(chip, host);
        entry = { el, chip, host, marker: new mapboxgl.Marker({ element: el }).setLngLat([t.lng, t.lat]).addTo(m) };
        // the chip is the button, with its own name; the wrapper is only a place on the map
        el.removeAttribute("role");
        el.removeAttribute("aria-label");
        tankMarkers.current.set(t.id, entry);
      }
      entry.marker.setLngLat([t.lng, t.lat]);
      const selected = t.id === selectedTankId;
      entry.el.classList.toggle(styles.tankMarkerSelected, selected);
      entry.chip.className = [styles.tank, styles[`tank_${t.state}`], selected ? styles.tankSelected : ""].join(" ");
      entry.chip.setAttribute("aria-pressed", String(selected));
      entry.chip.setAttribute("aria-label", t.title);
      entry.chip.title = t.title;
      (entry.chip.querySelector(`.${styles.gauge} span`) as HTMLSpanElement).style.height = `${t.gauge ?? 0}%`;
      (entry.chip.lastElementChild as HTMLSpanElement).textContent = t.text;
    }
    for (const [id, entry] of tankMarkers.current) {
      if (seen.has(id)) continue;
      entry.marker.remove();
      tankMarkers.current.delete(id);
    }
    setTankHost(selectedTankId ? (tankMarkers.current.get(selectedTankId)?.host ?? null) : null);
  }, [loaded, tanks, selectedTankId]);

  // zone labels and the pin: HTML markers (the raster base map has no fonts for map-drawn text)
  useEffect(() => {
    const m = map.current;
    const mapboxgl = lib.current;
    if (!m || !mapboxgl || !loaded) return;
    for (const mk of labelMarkers.current) mk.remove();
    labelMarkers.current = [];
    if (zoneLabels && !draft) {
      for (const z of zones) {
        const at = labelPoint(z.rings);
        if (!at) continue;
        const el = document.createElement("button");
        el.type = "button";
        // the zones' pills are how a zone is picked: say which one is open
        el.setAttribute("aria-pressed", String(z.id === selectedId));
        el.className = [
          styles.zoneLabel,
          z.id === selectedId ? styles.zoneLabelSelected : "",
          z.muted ? styles.zoneLabelMuted : "",
        ].join(" ");
        const name = document.createElement("strong");
        name.textContent = z.name;
        el.append(name);
        if (z.note) {
          const note = document.createElement("span");
          note.textContent = z.note;
          el.append(note);
        }
        el.addEventListener("click", (event) => {
          event.stopPropagation();
          handlers.current.onSelectZone?.(z.id);
        });
        labelMarkers.current.push(new mapboxgl.Marker({ element: el }).setLngLat(at).addTo(m));
        keepButton(el, z.note ? `${z.name}, ${z.note}` : z.name);
      }
    }
    for (const p of points) {
      if (!p.tag) continue;
      const el = document.createElement("span");
      el.className = styles.tag;
      el.textContent = p.tag;
      el.setAttribute("aria-hidden", "true");
      labelMarkers.current.push(
        new mapboxgl.Marker({ element: el, anchor: "left", offset: [10, 0] }).setLngLat([p.lng, p.lat]).addTo(m),
      );
    }
    if (pin) {
      const el = document.createElement("div");
      el.className = `${styles.pin} ${pin.tone === "danger" ? styles.pinDanger : ""}`;
      el.setAttribute("role", "img");
      el.setAttribute("aria-label", [pin.label, pin.note].filter(Boolean).join(": "));
      const callout = document.createElement("span");
      callout.className = styles.pinCallout;
      const name = document.createElement("strong");
      name.textContent = pin.label;
      callout.append(name);
      if (pin.note) {
        const note = document.createElement("span");
        note.textContent = pin.note;
        callout.append(note);
      }
      el.append(callout);
      labelMarkers.current.push(new mapboxgl.Marker({ element: el }).setLngLat([pin.lng, pin.lat]).addTo(m));
      // Mapbox names every marker "Map marker": say which place it is
      el.setAttribute("aria-label", [pin.label, pin.note].filter(Boolean).join(": "));
    }
    declutterTrucks.current?.();
  }, [loaded, zones, points, selectedId, zoneLabels, pin, draft]);

  /** Frame these points, or every zone and point. */
  const frame = (only: LngLat[] | null) => {
    const m = map.current;
    const mapboxgl = lib.current;
    if (!m || !mapboxgl) return;
    const all: LngLat[] = only?.length
      ? only
      : [
          ...zones.flatMap((z) => z.rings.flat()),
          ...points.map((p): LngLat => [p.lng, p.lat]),
          ...trucks.map((t): LngLat => [t.lng, t.lat]),
          ...tanks.map((t): LngLat => [t.lng, t.lat]),
        ];
    if (!all.length) return;
    const bounds = new mapboxgl.LngLatBounds(all[0], all[0]);
    for (const p of all) bounds.extend(p);
    // room for the overlay at the top, the legend at the bottom and a pin's callout to the right
    const padding = {
      top: children ? 64 : 48,
      bottom: legend ? 72 : 48,
      left: 48,
      right: pin ? 160 : 48,
    };
    m.fitBounds(bounds, { padding, maxZoom: 15, duration: 400 });
  };

  // look at what matters: only when the focus or the number of zones and points changes, not on every edit
  // biome-ignore lint/correctness/useExhaustiveDependencies: refitting on every vertex drag would fight the person editing
  useEffect(() => {
    if (loaded) frame(focus);
  }, [loaded, focus, zones.length, points.length, trucks.length, tanks.length]);

  const baseMap = BASE_MAPS.find((b) => b.style === style)?.id ?? null;
  const toggleLayer = (id: MapLayerId) =>
    setHidden((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className={[styles.frame, ...[...hidden].map((id) => styles[`hide_${id}`])].join(" ")} style={{ height }}>
      {accessToken ? (
        <div ref={box} className={styles.map} role="application" aria-label={label} />
      ) : (
        <p className={styles.noToken} role="status">
          The map needs a Mapbox access token. Set VITE_MAPBOX_TOKEN when building the app.
        </p>
      )}
      {children ? <div className={styles.overlay}>{children}</div> : null}
      {accessToken && !compact ? (
        <>
          <div className={styles.tools}>
            {fitAll ? (
              <button
                type="button"
                className={styles.tool}
                aria-label="Show every zone"
                title="Show every zone"
                onClick={() => frame(null)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </button>
            ) : null}
            <button
              type="button"
              className={styles.tool}
              aria-label="Back to Nairobi"
              title="Back to Nairobi"
              onClick={() => map.current?.flyTo({ center: NAIROBI_CENTER, zoom: NAIROBI_ZOOM, duration: 600 })}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M20 10c0 4.99-5.54 10.19-7.4 11.8a1 1 0 0 1-1.2 0C9.54 20.19 4 14.99 4 10a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.tool}
              aria-label="Map layers"
              title="Map layers"
              aria-expanded={layersOpen}
              onClick={() => setLayersOpen((open) => !open)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
                <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
              </svg>
            </button>
          </div>
          {layersOpen ? (
            // biome-ignore lint/a11y/noStaticElementInteractions: Escape closes the panel; its controls are the inputs
            <div
              className={styles.layersPanel}
              onKeyDown={(e) => {
                if (e.key === "Escape") setLayersOpen(false);
              }}
            >
              <fieldset>
                <legend>Base map</legend>
                {BASE_MAPS.map((b) => (
                  <label key={b.id}>
                    <input
                      type="radio"
                      name={`${label}-base-map`}
                      checked={baseMap === b.id}
                      onChange={() => {
                        setStyle(b.style);
                        saveBaseMap(b.id);
                      }}
                    />
                    {b.label}
                  </label>
                ))}
              </fieldset>
              {layers.length ? (
                <fieldset>
                  <legend>Show</legend>
                  {layers.map((l) => (
                    <label key={l.id}>
                      <input type="checkbox" checked={!hidden.has(l.id)} onChange={() => toggleLayer(l.id)} />
                      {l.label}
                    </label>
                  ))}
                </fieldset>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
      {legend && accessToken ? <div className={styles.legend}>{legend}</div> : null}
      {tankHost && tankCallout ? createPortal(tankCallout, tankHost) : null}
      {truckHost && truckCallout ? createPortal(truckCallout, truckHost) : null}
    </div>
  );
}

/**
 * Pick a point (a place, a fill site): click the map, or drag the pin. Zones show for context, so
 * it's clear which zone the point falls in.
 */
export function PointPicker({
  value,
  onChange,
  zones = [],
  height = "18rem",
  mapStyle,
}: {
  value: { lng: number; lat: number } | null;
  onChange: (at: { lng: number; lat: number }) => void;
  zones?: MapZone[];
  height?: string;
  mapStyle?: string;
}) {
  return (
    <ZoneMap
      label="Pick the point on the map"
      zones={zones.map((z) => ({ ...z, muted: true }))}
      points={value ? [{ id: "picked", ...value }] : []}
      pointsName="Chosen point"
      focus={value ? [[value.lng, value.lat]] : null}
      onMapClick={([lng, lat]) => onChange({ lng, lat })}
      height={height}
      mapStyle={mapStyle}
    />
  );
}
